/**
 * coupon-offers.js
 *
 * Behaviour for the "Available coupons" widgets rendered by
 * snippets/coupon-offers-product.liquid (PDP), snippets/coupon-offers-cart.liquid
 * (basket page and cart drawer) and snippets/coupon-offers-banner.liquid (slideshow
 * claim button). Logic without I/O lives in coupon-offers-core.js.
 *
 * - Click a card to apply its code through /cart/update.js, then re-render the cart
 *   sections the same way the theme's own coupon form does (cart:grouped-sections +
 *   the cartUpdate pubsub event).
 * - Remove an applied code from the basket widgets.
 * - Keep the PDP saving in step with the selected variant.
 * - A code applied from a PDP before the product is in the basket is kept by Shopify
 *   as "not applicable yet". If Shopify drops it (empty cart), it is stored here and
 *   applied once after the next cart change.
 *
 * Click handling is delegated from document, so cards that the cart re-renders keep
 * working without re-binding.
 */
(function () {
  'use strict';

  if (window.CouponOffers) return;

  const Core = window.CouponOffersCore;
  const PENDING_KEY = 'coupon-offers:pending';
  const PENDING_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

  let busy = false;

  function routes() {
    const r = (window.FoxTheme && FoxTheme.routes) || {};
    return {
      cart: (r.cart_url || '/cart') + '.js',
      update: r.cart_update_url || '/cart/update',
    };
  }

  async function getCart() {
    const response = await fetch(routes().cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  /** Replace the cart's codes with `codes` and return the cart, with re-rendered sections. */
  async function setCodes(codes) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );

    const body = new FormData();
    body.append('discount', codes.join(','));
    if (sections.length) {
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);
    }

    const response = await fetch(routes().update + '.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    if (!response.ok) throw new Error('Cart update failed');
    const updated = await response.json();

    // Same as the theme's coupon form: the update response is used for its sections,
    // and a fresh /cart.js read is what gets published.
    const cart = await getCart();
    cart.sections = updated.sections;
    return cart;
  }

  function publish(cart) {
    if (window.FoxTheme && FoxTheme.pubsub && cart.sections) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    }
  }

  function readPending() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(PENDING_KEY) || 'null');
      if (stored && stored.code && Date.now() - stored.at < PENDING_MAX_AGE) return stored.code;
    } catch (e) {
      // Storage blocked or corrupt: nothing pending.
    }
    clearPending();
    return null;
  }

  function writePending(code) {
    try {
      window.localStorage.setItem(PENDING_KEY, JSON.stringify({ code, at: Date.now() }));
    } catch (e) {
      // Storage blocked: the shopper can still apply the coupon in the basket.
    }
  }

  function clearPending() {
    try {
      window.localStorage.removeItem(PENDING_KEY);
    } catch (e) {
      // Nothing to clear.
    }
  }

  function setLoading(card, loading) {
    card.classList.toggle('is-loading', loading);
    if (loading) {
      card.setAttribute('aria-busy', 'true');
    } else {
      card.removeAttribute('aria-busy');
    }
  }

  function setStatus(offer, text, isError) {
    const status = offer.querySelector('.coupon-offer__status');
    if (!status) return;
    status.textContent = text || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  /** PDP only: show a card as applied (or applied and waiting for the product). */
  function markProductOffer(offer, state) {
    const widget = offer.closest('[data-coupon-context]');
    const card = offer.querySelector('[data-coupon-apply]');
    const applied = state === 'applied' || state === 'pending' || state === 'deferred';

    offer.classList.toggle('is-applied', applied);
    if (card) {
      if (applied) {
        card.setAttribute('aria-disabled', 'true');
      } else {
        card.removeAttribute('aria-disabled');
      }
    }
    if (!widget) return;
    if (state === 'applied') setStatus(offer, widget.dataset.textApplied);
    else if (state === 'pending' || state === 'deferred') setStatus(offer, widget.dataset.textPending);
    else setStatus(offer, '');
  }

  async function apply(offer) {
    const widget = offer.closest('[data-coupon-context]');
    const card = offer.querySelector('[data-coupon-apply]');
    const code = offer.dataset.couponCode;
    if (!widget || !card || !code || busy) return;
    if (card.getAttribute('aria-disabled') === 'true') return;

    const context = widget.dataset.couponContext;
    const productId = widget.dataset.productId;

    busy = true;
    setLoading(card, true);
    setStatus(offer, '');

    try {
      const before = await getCart();
      const existing = Core.cartCodes(before);
      let cart = await setCodes(Core.mergeCodes(existing, code));
      // A banner claim is for one product, like a product page claim.
      const classifyAs = context === 'banner' ? 'product' : context;
      const result = Core.classifyApplyResult(cart, code, { context: classifyAs, productId });

      if (result === 'rejected') {
        // Put the basket back as it was so a dud code does not sit on it.
        cart = await setCodes(Core.removeCode(Core.cartCodes(cart), code));
        publish(cart);
        // The cart widgets re-render on publish, so find this coupon's card again.
        const fresh = findOffer(widget.dataset.couponContext, code) || offer;
        setStatus(fresh, widget.dataset.textError, true);
        return;
      }

      if (result === 'deferred') writePending(code);
      else clearPending();

      if (context === 'product') markProductOffer(offer, result);
      publish(cart);

      // Banner claims continue to the product, where the coupon card shows as applied.
      if (widget.dataset.redirect) {
        window.location.assign(widget.dataset.redirect);
        return;
      }
    } catch (error) {
      console.error(error);
      setStatus(offer, widget.dataset.textError, true);
    } finally {
      setLoading(card, false);
      busy = false;
    }

    // Applied from the basket or drawer while a PDP is open: bring its cards up to date.
    if (context !== 'product') syncProductOffers().catch(() => {});
  }

  async function remove(offer) {
    const button = offer.querySelector('[data-coupon-remove]');
    const code = offer.dataset.couponCode;
    if (!button || !code || busy) return;

    busy = true;
    setLoading(offer.querySelector('.coupon-offer__card') || button, true);

    try {
      const before = await getCart();
      const cart = await setCodes(Core.removeCode(Core.cartCodes(before), code));
      clearPending();
      publish(cart);
    } catch (error) {
      console.error(error);
      const widget = offer.closest('[data-coupon-context]');
      if (widget) setStatus(offer, widget.dataset.textError, true);
    } finally {
      busy = false;
    }

    syncProductOffers().catch(() => {});
  }

  function findOffer(context, code) {
    const wanted = Core.normaliseCode(code);
    const offers = document.querySelectorAll(`[data-coupon-context="${context}"] .coupon-offer`);
    return Array.from(offers).find((o) => Core.normaliseCode(o.dataset.couponCode) === wanted) || null;
  }

  /** Bring PDP cards in line with what the cart really holds. */
  async function syncProductOffers(cart) {
    const widgets = document.querySelectorAll('[data-coupon-context="product"]');
    if (!widgets.length) return;

    const current = cart || (await getCart());
    const pending = readPending();

    widgets.forEach((widget) => {
      widget.querySelectorAll('.coupon-offer').forEach((offer) => {
        const code = offer.dataset.couponCode;
        const entry = Core.findCode(current, code);
        if (entry) {
          markProductOffer(
            offer,
            Core.classifyApplyResult(current, code, { context: 'product', productId: widget.dataset.productId })
          );
        } else if (pending && Core.normaliseCode(pending) === Core.normaliseCode(code)) {
          markProductOffer(offer, 'deferred');
        } else {
          markProductOffer(offer, 'none');
        }
      });
    });
  }

  /** A code Shopify dropped from an empty cart gets one retry once the cart has items. */
  async function retryPending() {
    const pending = readPending();
    if (!pending || busy) return;

    busy = true;
    try {
      const cart = await getCart();
      if (!cart.item_count) return;

      clearPending();
      if (Core.findCode(cart, pending)) return;

      const updated = await setCodes(Core.mergeCodes(Core.cartCodes(cart), pending));
      publish(updated);
    } catch (error) {
      console.error(error);
    } finally {
      busy = false;
    }
  }

  function onVariantChange(event) {
    const data = event && event.data;
    if (!data || !data.variant) return;

    const widgets = document.querySelectorAll(`[data-coupon-context="product"][data-section-id="${data.sectionId}"]`);
    const moneyFormat = window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat;

    widgets.forEach((widget) => {
      widget.querySelectorAll('.coupon-offer').forEach((offer) => {
        const target = offer.querySelector('[data-coupon-saving]');
        if (!target || !FoxTheme.Currency) return;
        const saving = Core.computeSaving(offer.dataset.couponType, offer.dataset.couponValue, data.variant.price);
        target.textContent = FoxTheme.Currency.formatMoney(saving, moneyFormat);
      });
    });
  }

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-coupon-remove]');
    if (removeButton) {
      event.preventDefault();
      const offer = removeButton.closest('.coupon-offer');
      if (offer) remove(offer);
      return;
    }

    const applyButton = event.target.closest('[data-coupon-apply]');
    if (applyButton) {
      event.preventDefault();
      const offer = applyButton.closest('.coupon-offer');
      if (offer) apply(offer);
    }
  });

  function init() {
    if (window.FoxTheme && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, onVariantChange);
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
        if (busy) return;
        retryPending().then(() => syncProductOffers().catch(() => {}));
      });
    }
    syncProductOffers().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CouponOffers = { apply, remove, syncProductOffers };
})();
